import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductSearch from './components/ProductSearch';
import ShoppingCart from './components/ShoppingCart';
import PaymentModal from './components/PaymentModal';
import TransactionHistory from './components/TransactionHistory';
import SalesMetrics from './components/SalesMetrics';


import { emailReceiptService } from '../../services/emailReceiptService';

const PointOfSaleDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, loading: authLoading, profileLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dailyStats, setDailyStats] = useState({
    totalSales: 0,
    transactionCount: 0,
    averageTransaction: 0
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [activeTab, setActiveTab] = useState('pos');

  useEffect(() => {
    if (!authLoading && !profileLoading && (!user || (!isGymOwner && !isStaff && !isTrainer))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isStaff, isTrainer, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isStaff || isTrainer)) {
      loadPOSData();
    }
  }, [currentTenant, isGymOwner, isStaff, isTrainer]);

  const loadPOSData = async () => {
    setLoading(true);
    try {
      // Load active products
      const { data: productsData, error: productsError } = await supabase
        ?.from('products')
        ?.select('*, product_categories(name)')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('status', 'active')
        ?.order('name', { ascending: true });

      if (productsError) throw productsError;
      setProducts(productsData || []);

      // Load today's sales stats
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data: statsData, error: statsError } = await supabase
        ?.from('pos_daily_sales')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('sale_date', today)
        ?.single();

      if (!statsError && statsData) {
        setDailyStats({
          totalSales: parseFloat(statsData?.total_revenue || 0),
          transactionCount: statsData?.total_transactions || 0,
          averageTransaction: statsData?.total_transactions > 0 
            ? parseFloat(statsData?.total_revenue) / statsData?.total_transactions 
            : 0
        });
      }

    } catch (error) {
      console.error('Load POS data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product, quantity = 1) => {
    const existingItem = cart?.find(item => item?.product?.id === product?.id);
    
    if (existingItem) {
      // Update quantity
      setCart(cart?.map(item => 
        item?.product?.id === product?.id
          ? { ...item, quantity: item?.quantity + quantity }
          : item
      ));
    } else {
      // Add new item
      setCart([...cart, { product, quantity }]);
    }
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(productId);
    } else {
      setCart(cart?.map(item => 
        item?.product?.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const handleRemoveFromCart = (productId) => {
    setCart(cart?.filter(item => item?.product?.id !== productId));
  };

  const handleClearCart = () => {
    setCart([]);
    setSelectedMember(null);
  };

  const handleCheckout = () => {
    if (cart?.length === 0) return;
    setShowPaymentModal(true);
  };

  const handleCompletePayment = async (paymentData) => {
    try {
      const transactionNumber = `POS-${Date.now()}`;
      const subtotal = cart?.reduce((sum, item) => {
        const price = parseFloat(item?.product?.base_price);
        return sum + (price * item?.quantity);
      }, 0);

      const tax = cart?.reduce((sum, item) => {
        const price = parseFloat(item?.product?.base_price);
        const taxRate = parseFloat(item?.product?.tax_rate || 0) / 100;
        return sum + (price * item?.quantity * taxRate);
      }, 0);

      const total = subtotal + tax - paymentData?.discount;

      // Create transaction
      const { data: transaction, error: transactionError } = await supabase?.from('pos_transactions')?.insert([{
          tenant_id: currentTenant?.id,
          transaction_number: transactionNumber,
          cashier_id: user?.id,
          customer_id: selectedMember?.id || null,
          subtotal,
          tax_amount: tax,
          discount_amount: paymentData?.discount,
          total_amount: total,
          payment_method: paymentData?.paymentMethod,
          payment_status: 'completed',
          notes: paymentData?.notes
        }])?.select()?.single();

      if (transactionError) throw transactionError;

      // Insert transaction items
      const items = cart?.map(item => ({
        transaction_id: transaction?.id,
        product_id: item?.product?.id,
        product_name: item?.product?.name,
        quantity: item?.quantity,
        unit_price: item?.product?.base_price,
        discount_amount: 0,
        subtotal: item?.product?.base_price * item?.quantity
      }));

      const { error: itemsError } = await supabase?.from('pos_transaction_items')?.insert(items);

      if (itemsError) throw itemsError;

      // Update inventory
      for (const item of cart) {
        if (item?.product?.track_inventory) {
          const { error: inventoryError } = await supabase?.from('pos_products')?.update({ 
              stock_quantity: item?.product?.stock_quantity - item?.quantity,
              updated_at: new Date()?.toISOString()
            })?.eq('id', item?.product?.id);

          if (inventoryError) throw inventoryError;

          // Log inventory change
          await supabase?.from('pos_inventory_logs')?.insert([{
            tenant_id: currentTenant?.id,
            product_id: item?.product?.id,
            quantity_change: -item?.quantity,
            quantity_before: item?.product?.stock_quantity,
            quantity_after: item?.product?.stock_quantity - item?.quantity,
            reason: 'sale',
            reference_id: transaction?.id,
            created_by: user?.id
          }]);
        }
      }

      // Record cash transaction if payment method is cash and drawer is open
      if (paymentData?.paymentMethod === 'cash' && activeDrawer) {
        const { error: cashError } = await supabase?.from('cash_transactions')?.insert([{
            tenant_id: currentTenant?.id,
            drawer_id: activeDrawer?.id,
            transaction_type: 'sale',
            amount: total,
            balance_before: activeDrawer?.current_balance,
            balance_after: activeDrawer?.current_balance + total,
            reference_type: 'pos_transaction',
            reference_id: transaction?.id,
            notes: `POS Sale - ${transactionNumber}${paymentData?.changeGiven ? ` | Change: $${paymentData?.changeGiven?.toFixed(2)}` : ''}`,
            created_by: user?.id
          }]);

        if (cashError) throw cashError;
      }

      // Send email receipt if customer email available
      if (selectedMember?.email) {
        try {
          await emailReceiptService?.sendPOSReceipt(
            transaction?.id,
            currentTenant?.id
          );
        } catch (emailError) {
          console.error('Email receipt error:', emailError);
        }
      }

      setCart([]);
      setSelectedMember(null);
      setShowPaymentModal(false);
      setRefreshTrigger(prev => prev + 1);
      alert('Payment completed successfully!');
    } catch (err) {
      console.error('Payment error:', err);
      alert(err?.message || 'Failed to complete payment');
      throw err;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading POS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-6 pb-24 lg:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-foreground">Point of Sale</h1>
            <Button
              variant="ghost"
              onClick={() => navigate('/product-catalog-management')}
            >
              <Icon name="Package" size={18} className="mr-2" />
              Manage Catalog
            </Button>
          </div>
          <p className="text-muted-foreground">
            Process sales and manage transactions
          </p>
        </div>

        {/* Daily Stats */}
        <SalesMetrics stats={dailyStats} />

        {/* Main POS Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Product Search & Selection */}
          <div className="lg:col-span-2">
            <ProductSearch
              products={products}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onAddToCart={handleAddToCart}
            />
          </div>

          {/* Shopping Cart */}
          <div>
            <ShoppingCart
              cart={cart}
              selectedMember={selectedMember}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveFromCart}
              onClearCart={handleClearCart}
              onSelectMember={setSelectedMember}
              onCheckout={handleCheckout}
            />
          </div>
        </div>

        {/* Recent Transactions */}
        <TransactionHistory tenantId={currentTenant?.id} />
      </main>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          cart={cart}
          selectedMember={selectedMember}
          onClose={() => setShowPaymentModal(false)}
          onComplete={handleCompletePayment}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default PointOfSaleDashboard;